/* © CNES */
import moment from 'moment';
import { Extent } from 'ol/extent';
import { refMissions } from '../../../../../assets/config/filters/Referenciel';
import { configContextInitialValue } from '../../../../../context/ConfigContext';
import { ItemFilter } from '../../../../../models/Filter';
import { GeoJsonPolygon } from '../../../../../models/GeoJsonPolygon';
import {
  DATASET,
  END_DATE_TIME,
  GRID_CODE,
  IDENTIFIER,
  INSTRUMENT_MODE,
  ORBIT_ABSOLUTE,
  ORBIT_RELATIVE,
  PLATFORM,
  PPARAMETER,
  PRODUCT_ACQUISITION_TYPE,
  PRODUCT_TYPE,
  START_DATE_TIME,
  VISIBILITY,
} from '../../../../shared/Constants';
import { filterSubSectionsOfMission } from '../../../../shared/Utils';

/**
 * get the list of dynamic filter to display as tag.
 * @param filterData ItemFilter context.
 * @param currentView currentView mode activated or not.
 * @returns dynamicFilters
 */
export const getDynamicFilters = (
  filterData: ItemFilter,
  currentView: boolean,
  searchZone: { type: string | null; value: GeoJsonPolygon | null },
  drawnShape: Extent | undefined
) => {
  let dynamicFilterList: { label: string; key: string }[] = [];
  Object.keys(filterData)
    .filter(Boolean)
    .forEach((el) => {
      switch (el) {
        case START_DATE_TIME:
          filterData[el] &&
            dynamicFilterList.push({
              label: `Before: ${moment(filterData[el]?.slice(0, -1)).format(
                'YYYY-MM-DD HH:mm'
              )}`,
              key: el,
            });
          break;
        case ORBIT_RELATIVE:
          filterData[el] &&
            Array.from(filterData[el] as string[]).forEach((element) => {
              dynamicFilterList.push({
                label: `Rel. Orbit : ${element}`,
                key: el,
              });
            });
          break;
        case ORBIT_ABSOLUTE:
          filterData[el] &&
            Array.from(filterData[el] as string[]).forEach((element) => {
              dynamicFilterList.push({
                label: `Abs. Orbit : ${element}`,
                key: el,
              });
            });
          break;
        case GRID_CODE:
          filterData[el] &&
            dynamicFilterList.push({
              label: `Tile ID : ${filterData[el]}`,
              key: el,
            });
          break;
        case END_DATE_TIME:
          filterData[el] &&
            dynamicFilterList.push({
              label: `After: ${moment(filterData[el]?.slice(0, -1)).format(
                'YYYY-MM-DD HH:mm'
              )}`,
              key: el,
            });
          break;
        case DATASET:
          dynamicFilterList = handleDataSet(
            filterData[el] as string[],
            dynamicFilterList
          );
          break;
        case PLATFORM:
        case 'dataProduct':
        case INSTRUMENT_MODE:
        case VISIBILITY:
        case PRODUCT_ACQUISITION_TYPE:
        case PPARAMETER:
          const filter = filterData[el];
          Array.isArray(filter) &&
            filter.forEach((value) => {
              dynamicFilterList.push({ label: value, key: el });
            });
          break;
        //We display productType values in dynamicFilter only for Postel or GEOV2 AVHRR collections
        case PRODUCT_TYPE:
          if (
            dynamicFilterList.find(
              (filter) =>
                filter.label === 'Postel' || filter.label === 'GEOV2 AVHRR'
            )
          ) {
            const filter = filterData[el];
            Array.isArray(filter) &&
              filter.forEach((value) => {
                dynamicFilterList.push({ label: value, key: el });
              });
          }
          break;
        case IDENTIFIER:
          const productNameValue = filterData[el] as string;
          productNameValue &&
            dynamicFilterList.push({
              label: productNameValue.toUpperCase(),
              key: el,
            });
          break;
      }
    });

  if (currentView) {
    dynamicFilterList.push({
      label: 'Current View',
      key: 'CurrentView',
    });
  }

  if (searchZone.value && searchZone.type) {
    dynamicFilterList.push({ label: searchZone.type, key: searchZone.type });
  }

  if (drawnShape) {
    dynamicFilterList.push({
      label: 'Hand-drawn area',
      key: 'HandDrawnArea',
    });
  }

  return dynamicFilterList;
};

/**
 * Handle Data Sets.
 * @param dataSets dataSets table.
 * @param dynamicFilterList dynamic Filter List.
 * @returns dynamic Filter List.
 */
const handleDataSet = (
  dataSets: string[],
  dynamicFilterList: {
    label: string;
    key: string;
  }[]
) => {
  //handle case of POSTEL or GEOV2 VHRR product
  if (dataSets?.find((a) => a.includes('POSTEL'))) {
    const refMission = refMissions.find((refMission) => {
      return JSON.stringify(refMission.key) === JSON.stringify(dataSets);
    });
    dynamicFilterList.push({
      label: refMission?.name ?? '',
      key: DATASET,
    });
  } else {
    dataSets?.forEach((dataSet) => {
      const refMission = refMissions.find((refMission) => {
        return refMission.key.includes(dataSet);
      });
      if (
        !dynamicFilterList.find(
          (dynFilter) => dynFilter.label === refMission?.name
        )
      ) {
        dynamicFilterList.push({
          label: refMission?.name ?? '',
          key: DATASET,
        });
      }
    });
  }

  return dynamicFilterList;
};

/**
 * Handle both relative and absolute OrbitID case.
 * @param filterData ItemFilter
 * @param item { label: string; key: string }
 * @returns ItemFilter
 */
const handleOrbitID = (
  filterData: ItemFilter,
  item: { label: string; key: string }
) => {
  const filterRelOrbit: string[] = item.label.split(':');
  const newFilterRelOrbit = (filterData[item.key] as string[]).filter(
    (element) => element !== filterRelOrbit[1].trim()
  );
  return { ...filterData, ...{ [item.key]: newFilterRelOrbit } };
};

/**
 * used to filter the list of data after remove item
 * @param filterData
 * @param mapExtent
 * @param item
 * @returns
 */
export const getUpdatedFilterData = (
  filterData: ItemFilter,
  mapExtent: { extent: number[]; currentView: boolean; bbox: number[] },
  item: { label: string; key: string }
) => {
  let newFilterData = filterData;
  let itemToUpdate = filterData[item.key];
  let newMapExtent = mapExtent;
  let selectedMission = null;
  let missionValue: number[] | null = null;

  switch (item.key) {
    case 'CurrentView':
      newMapExtent = {
        extent: configContextInitialValue.mapExtent.extent,
        currentView: false,
        bbox: configContextInitialValue.mapExtent.extent,
      };
      break;
    case DATASET:
      const missionKey = refMissions.find((el) => el.name === item.label)?.key;
      //filter mission item with removed key
      if (Array.isArray(itemToUpdate)) {
        itemToUpdate = itemToUpdate?.filter((el) => !missionKey?.includes(el));
      }
      // convert filtered mission keys to mission values
      missionValue = refMissions
        .filter((el) => itemToUpdate?.includes(el.key[0]))
        .map((el) => el.value);
      selectedMission = missionValue.length ? missionValue : null;
      itemToUpdate = itemToUpdate?.length ? itemToUpdate : null;
      newFilterData = {
        ...filterData,
        ...filterSubSectionsOfMission(missionValue, filterData),
        ...{ [item.key]: itemToUpdate },
      };
      break;
    case PLATFORM:
      if (Array.isArray(itemToUpdate)) {
        itemToUpdate = itemToUpdate?.filter((el) => item.label !== el);
      }
      missionValue = refMissions.map((mission) => mission.value);
      newFilterData = {
        ...filterData,
        ...filterSubSectionsOfMission(missionValue, filterData),
        ...{
          [item.key]: itemToUpdate,
        },
      };
      break;
    case ORBIT_RELATIVE:
    case ORBIT_ABSOLUTE:
      newFilterData = handleOrbitID(filterData, item);
      break;
    case 'dataProduct':
    case INSTRUMENT_MODE:
    case VISIBILITY:
    case PRODUCT_ACQUISITION_TYPE:
    case PRODUCT_TYPE:
      itemToUpdate = filterData[item.key];
      if (Array.isArray(itemToUpdate)) {
        itemToUpdate = itemToUpdate?.filter((el) => el !== item.label);
      }
      itemToUpdate = itemToUpdate?.length ? itemToUpdate : null;
      newFilterData = { ...filterData, ...{ [item.key]: itemToUpdate } };
      break;
    default:
      newFilterData = { ...filterData, ...{ [item.key]: null } };
      break;
  }

  return { newFilterData, newMapExtent, selectedMission };
};
